<script setup lang="ts">
import { ref } from "vue";

const showTerms = ref(false);

const retailers = [
  { name: "Kmart", logo: "/images/onepass-perks/logo-kmart.svg", width: 93, height: 28 },
  { name: "Target", logo: "/images/onepass-perks/logo-target.svg", width: 96, height: 30 },
  { name: "Bunnings Warehouse", logo: "/images/onepass-perks/logo-bunnings.svg", width: 96, height: 30 },
  { name: "Officeworks", logo: "/images/onepass-perks/logo-officeworks.svg", width: 108, height: 22 },
  { name: "InstantScripts", logo: "/images/onepass-perks/logo-instantscripts.svg", width: 152, height: 24 },
  { name: "Priceline Pharmacy", logo: "/images/onepass-perks/logo-priceline.svg", width: 143, height: 30 },
];

const benefits = [
  {
    icon: "/images/onepass-perks/icon-truck.svg",
    title: "Fast & free delivery",
    titleSup: "1",
    link: "https://onepass.com.au/benefits/free-delivery",
    description:
      "With no minimum spend on eligible items or orders, enjoy free delivery each time you shop online at Kmart, Target, Officeworks, Bunnings Warehouse, InstantScripts and Priceline.",
  },
  {
    icon: "/images/onepass-perks/icon-flybuys.svg",
    title: "5x Flybuys points",
    titleSup: "2",
    link: "https://onepass.com.au/benefits/5x-flybuys-points",
    description:
      "Shopping at Kmart, Target, Bunnings Warehouse and Officeworks? Collect 5x Flybuys points for every $1 spent in-store and with Click & Collect. Simply link and scan your Flybuys card to get started.",
  },
  {
    icon: "/images/onepass-perks/icon-returns.svg",
    title: "365 day change of mind returns",
    link: "https://onepass.com.au/benefits/365-day-returns",
    description:
      "Enjoy more peace of mind knowing you can shop Kmart, Target, Officeworks and Priceline and you have 365 days (from the date of purchase) to return your unused items in-store for a full refund.",
  },
  {
    icon: "/images/onepass-perks/icon-exclusives.svg",
    title: "Member exclusives",
    link: "https://onepass.com.au/benefits",
    description:
      "Express Click & Collect at Kmart and Bunnings Warehouse, discounts on InstantScripts, extra loyalty rewards at Priceline and early access to selected sales and events are just the beginning.",
  },
];
</script>

<template>
  <section class="perks-section">
    <!-- Mascot (positioned absolute, overflows left) -->
    <img
      src="/images/onepass-perks/mascot-holding-phone.png"
      alt="OnePass Mascot"
      class="perks-mascot"
    />

    <!-- Heading -->
    <h2 class="perks-heading">
      Enjoy all the perks of Australia's favourite retailers<br />
      with your FREE OnePass membership
    </h2>

    <!-- Subtitle -->
    <p class="perks-subtitle">
      From savings to rewards, get more value from the brands you love.
    </p>

    <!-- Retailer logos bar -->
    <div class="retailer-logos">
      <img
        v-for="retailer in retailers"
        :key="retailer.name"
        :src="retailer.logo"
        :alt="retailer.name"
        :width="retailer.width"
        :height="retailer.height"
        class="retailer-logo"
      />
    </div>

    <!-- Sub-heading -->
    <h3 class="perks-subheading">
      OnePass members get the best of both worlds
    </h3>

    <!-- Benefits grid: 2x2 desktop, stacked mobile -->
    <div class="benefits-grid">
      <div
        v-for="(benefit, index) in benefits"
        :key="index"
        class="benefit-card"
      >
        <img
          :src="benefit.icon"
          :alt="benefit.title"
          width="24"
          height="24"
          class="benefit-icon"
        />
        <h4 class="benefit-title">
          <a :href="benefit.link" target="_blank" rel="noopener" class="benefit-title-link">
            {{ benefit.title }}<sup v-if="benefit.titleSup">{{ benefit.titleSup }}</sup>
          </a>
        </h4>
        <p class="benefit-description">{{ benefit.description }}</p>
      </div>
    </div>

    <!-- Terms & Conditions accordion -->
    <button class="terms-toggle" @click="showTerms = !showTerms">
      <span class="terms-toggle-text">Terms &amp; Conditions</span>
      <svg
        :class="['terms-toggle-icon', { 'is-open': showTerms }]"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 1.33337V14.6667M1.33337 8.00004H14.6667"
          stroke="#000000"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <Transition name="terms-expand">
      <div v-if="showTerms" class="terms-content">
        <p>
          <sup>1</sup> T&amp;C's, exclusions apply. For InstantScripts, delivery is available for prescription medicines obtained through the online prescriptions service and treatment plans only. Prescription medications obtained through scripts provided via telehealth consultations are not eligible for delivery. Standard delivery only, express delivery not eligible for free delivery.
        </p>
        <p>
          <sup>2</sup> T&amp;Cs, exclusions and eligibility criteria apply. Free delivery with no minimum spend. Express Click &amp; Collect already available to all customers at Target, Officeworks and Priceline. <a href="https://onepass.com.au/benefits/instantscripts" target="_blank" rel="noopener">Click here for benefits and exclusions for InstantScripts</a>. OnePass only available at Priceline to Sister Club members. Flybuys offer not available at InstantScripts and Priceline. Savings compared to separately purchasing OnePass monthly subscriptions. See <a href="https://onepass.com.au/terms" target="_blank" rel="noopener">T&amp;Cs</a> for full details.
        </p>
      </div>
    </Transition>
  </section>
</template>

<style scoped lang="scss">
/* ═══════════════════════════════════════════════════════ */
/* Design tokens (from Figma)                             */
/* ═══════════════════════════════════════════════════════ */
$wisteria: #F4E7FF;
$royal: #3A016A;
$purple: #9900F1;
$white: #FFFFFF;
$black: #000000;

/* ═══════════════════════════════════════════════════════ */
/* Section wrapper                                        */
/* Figma: 1440px wide, padding 64px 210px, gap 24px      */
/* bg: #F4E7FF (wisteria)                                 */
/* ═══════════════════════════════════════════════════════ */
.perks-section {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 210px;
  gap: 24px;
  width: 100%;
  background: $wisteria;
  font-family: "Montserrat", sans-serif;
  overflow: hidden;
}

/* ═══════════════════════════════════════════════════════ */
/* Mascot — absolute, left side, overlaps content         */
/* Figma: 268×432px, left: 80px, top: 124px              */
/* ═══════════════════════════════════════════════════════ */
.perks-mascot {
  position: absolute;
  left: -10px;
  top: 124px;
  width: 268px;
  height: auto;
  object-fit: contain;
  z-index: 30;
  pointer-events: none;
}

/* ═══════════════════════════════════════════════════════ */
/* Heading                                                */
/* Figma: H2 — 24px/30px, 700, center, #3A016A           */
/* width: 808px                                           */
/* ═══════════════════════════════════════════════════════ */
.perks-heading {
  max-width: 808px;
  font-weight: 700;
  font-size: 24px;
  line-height: 30px;
  text-align: center;
  letter-spacing: -0.01em;
  color: $royal;
  margin: 0;
  z-index: 2;
}

/* ═══════════════════════════════════════════════════════ */
/* Subtitle                                               */
/* Figma: M — 16px/24px, 400, center, #3A016A            */
/* width: 1020px (stretch)                                */
/* ═══════════════════════════════════════════════════════ */
.perks-subtitle {
  width: 100%;
  max-width: 1020px;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: $royal;
  margin: 0;
  z-index: 2;
}

/* ═══════════════════════════════════════════════════════ */
/* Retailer logos bar                                     */
/* Figma: 954×56px, bg #FFF, radius 8px                  */
/* padding 12px 16px, gap 24px, centered                  */
/* ═══════════════════════════════════════════════════════ */
.retailer-logos {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 32px;
  gap: 16px;

  background: $white;
  border-radius: 8px;
  z-index: 12;
  position: relative;
}

.retailer-logo {
  flex: none;
  object-fit: contain;
  max-width: 100%;
}

/* ═══════════════════════════════════════════════════════ */
/* Sub-heading                                            */
/* Figma: XL — 20px/28px, 700, center, #3A016A           */
/* width: 1020px                                          */
/* ═══════════════════════════════════════════════════════ */
.perks-subheading {
  width: 100%;
  max-width: 1020px;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
  color: $royal;
  margin: 0;
  z-index: 2;
}

/* ═══════════════════════════════════════════════════════ */
/* Benefits grid                                          */
/* Figma: 2 rows × 2 cols, 954px wide                    */
/* Each row: flex-direction: row, gap: 24px               */
/* ═══════════════════════════════════════════════════════ */
.benefits-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  max-width: 954px;
  width: 100%;
  z-index: 2;
}

/* ═══════════════════════════════════════════════════════ */
/* Benefit card                                           */
/* Figma: padding 24px, gap 12px, bg #FFF, radius 12px   */
/* width: 465px (half of 954-24gap)                       */
/* ═══════════════════════════════════════════════════════ */
.benefit-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  gap: 12px;
  background: $white;
  border-radius: 12px;
}

.benefit-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

/* ═══════════════════════════════════════════════════════ */
/* Benefit title                                          */
/* Figma: L — 18px/26px, 700, #3A016A                    */
/* ═══════════════════════════════════════════════════════ */
.benefit-title {
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  color: $royal;
  margin: 0;

  sup {
    font-size: 0.6em;
    vertical-align: super;
  }
}

.benefit-title-link {
  color: $royal;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

/* ═══════════════════════════════════════════════════════ */
/* Benefit description                                    */
/* Figma: M — 16px/24px, 400, #3A016A                    */
/* ═══════════════════════════════════════════════════════ */
.benefit-description {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: $royal;
  margin: 0;
}

/* ═══════════════════════════════════════════════════════ */
/* Terms & Conditions toggle                              */
/* Figma: padding 12px 16px, gap 16px, radius 8px        */
/* Text: L — 18px/26px, 700, #3A016A                     */
/* Icon: 16×16, #000                                      */
/* ═══════════════════════════════════════════════════════ */
.terms-toggle {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 16px;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  z-index: 2;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.terms-toggle-text {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  color: $royal;
}

.terms-toggle-icon {
  transition: transform 0.3s ease;

  &.is-open {
    transform: rotate(45deg);
  }
}

/* ═══════════════════════════════════════════════════════ */
/* Terms content (expandable)                             */
/* ═══════════════════════════════════════════════════════ */
.terms-content {
  max-width: 954px;
  width: 100%;
  z-index: 2;

  p {
    font-family: "Montserrat", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: $royal;
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }

    sup {
      font-size: 0.7em;
      vertical-align: super;
      font-weight: 700;
    }

    a {
      color: $purple;
      text-decoration: underline;
    }
  }
}

/* Terms expand transition */
.terms-expand-enter-active,
.terms-expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.terms-expand-enter-from,
.terms-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.terms-expand-enter-to,
.terms-expand-leave-from {
  max-height: 500px;
}

/* ═══════════════════════════════════════════════════════ */
/* TABLET (≤1280px)                                       */
/* ═══════════════════════════════════════════════════════ */
@media (max-width: 1280px) {
  .perks-section {
    padding: 64px 80px;
  }

  .perks-mascot {
    left: -10px;
    top: 100px;
    width: 200px;
  }
}

/* ═══════════════════════════════════════════════════════ */
/* TABLET SMALL (≤1024px)                                 */
/* ═══════════════════════════════════════════════════════ */
@media (max-width: 1024px) {
  .perks-section {
    padding: 48px 40px;
  }

  .perks-mascot {
    display: none;
  }
}

/* ═══════════════════════════════════════════════════════ */
/* MOBILE (≤768px)                                        */
/* Figma mobile: 420px, padding 64px 24px, gap 24px       */
/* Cards stack vertically, logos wrap                      */
/* ═══════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  .perks-section {
    padding: 64px 24px;
    gap: 24px;
  }

  .perks-heading {
    max-width: 372px;
    font-size: 24px;
    line-height: 30px;
  }

  .perks-subtitle {
    max-width: 372px;
  }

  .retailer-logos {
    flex-wrap: wrap;
    padding: 16px;
    gap: 24px;
    max-width: 372px;
  }

  .perks-subheading {
    max-width: 372px;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
    max-width: 372px;
  }

  .terms-toggle {
    width: 100%;
    max-width: 372px;
  }

  .terms-content {
    max-width: 372px;
  }
}
</style>
